// Re-export from shared package for backwards compatibility
export { extractUrls } from "@ano/shared/utils";

const YOUTUBE_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
];

const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];

const LOOM_HOSTS = ["loom.com", "www.loom.com"];

const TIKTOK_HOSTS = [
  "tiktok.com",
  "www.tiktok.com",
  "m.tiktok.com",
  "vm.tiktok.com",
];

function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Check if a URL is from a supported video platform. */
export function isVideoUrl(url: string): boolean {
  const hostname = getHostname(url);
  if (!hostname) return false;
  return (
    YOUTUBE_HOSTS.includes(hostname) ||
    VIMEO_HOSTS.includes(hostname) ||
    LOOM_HOSTS.includes(hostname) ||
    TIKTOK_HOSTS.includes(hostname)
  );
}

/** Extract the video ID from a YouTube URL, or null if not a YouTube URL. */
export function getYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (!YOUTUBE_HOSTS.includes(hostname)) return null;

    // youtu.be/VIDEO_ID
    if (hostname === "youtu.be") {
      const id = parsed.pathname.slice(1).split("/")[0];
      return id || null;
    }

    // youtube.com/watch?v=VIDEO_ID
    const vParam = parsed.searchParams.get("v");
    if (vParam) return vParam;

    // youtube.com/shorts/VIDEO_ID, /embed/VIDEO_ID, /v/VIDEO_ID
    const pathMatch = parsed.pathname.match(
      /^\/(shorts|embed|v)\/([A-Za-z0-9_-]+)/,
    );
    if (pathMatch) return pathMatch[2];

    return null;
  } catch {
    return null;
  }
}

/** Extract the video ID from a Vimeo URL, or null if not a Vimeo URL. */
export function getVimeoVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (!VIMEO_HOSTS.includes(hostname)) return null;

    // player.vimeo.com/video/VIDEO_ID
    const playerMatch = parsed.pathname.match(/^\/video\/(\d+)/);
    if (playerMatch) return playerMatch[1];

    // vimeo.com/VIDEO_ID or vimeo.com/channels/NAME/VIDEO_ID
    // or vimeo.com/groups/NAME/videos/VIDEO_ID
    const segments = parsed.pathname.split("/").filter(Boolean);
    for (let i = segments.length - 1; i >= 0; i--) {
      if (/^\d+$/.test(segments[i])) return segments[i];
    }

    return null;
  } catch {
    return null;
  }
}

/** Extract the video ID from a Loom URL, or null if not a Loom URL. */
export function getLoomVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (!LOOM_HOSTS.includes(hostname)) return null;

    // loom.com/share/VIDEO_ID or loom.com/embed/VIDEO_ID
    const match = parsed.pathname.match(/^\/(share|embed)\/([a-f0-9]+)/);
    return match ? match[2] : null;
  } catch {
    return null;
  }
}

/** Extract the video ID from a TikTok URL, or null if not a TikTok URL. */
export function getTikTokVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (!TIKTOK_HOSTS.includes(hostname)) return null;

    // tiktok.com/@user/video/VIDEO_ID
    const videoMatch = parsed.pathname.match(/\/video\/(\d+)/);
    if (videoMatch) return videoMatch[1];

    // vm.tiktok.com/SHORT_CODE (short links have a code, not a numeric ID)
    if (hostname === "vm.tiktok.com") {
      const code = parsed.pathname.slice(1).split("/")[0];
      // Validate code is alphanumeric to prevent injection via embed URL
      if (code && /^[a-zA-Z0-9_-]+$/.test(code)) return code;
      return null;
    }

    return null;
  } catch {
    return null;
  }
}

/** Get a thumbnail URL for a video derived from the URL itself (no API call). */
export function getVideoThumbnailUrl(url: string): string | null {
  const ytId = getYouTubeVideoId(url);
  if (ytId) {
    return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  }

  return null;
}

/** Get the embeddable iframe URL for a video, or null if not a supported video. */
export function getVideoEmbedUrl(url: string): string | null {
  const ytId = getYouTubeVideoId(url);
  if (ytId) {
    // Use youtube.com (not youtube-nocookie.com): nocookie rejects unusual
    // parent origins with player error 153 in packaged Electron where the
    // parent is `ano-app://localhost`. Dev works on localhost:1420.
    return `https://www.youtube.com/embed/${ytId}?autoplay=1`;
  }

  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
  }

  const loomId = getLoomVideoId(url);
  if (loomId) {
    return `https://www.loom.com/embed/${loomId}?autoplay=1`;
  }

  const tiktokId = getTikTokVideoId(url);
  if (tiktokId) {
    return `https://www.tiktok.com/embed/v2/${tiktokId}`;
  }

  return null;
}
